import { FaceCullingInfo, PhotoRecord } from "@/lib/catalog"
import { BlurryRejectThreshold, FaceRejectThreshold } from "@/lib/catalog/types"
import { useTranslation } from "react-i18next"
import i18n from "i18next"
import {
	extractFacesFromLoadedPhotoIfNeeded,
	blurryPhotoThresholdAtom,
	currentCullingInfoAtom,
	poorFaceQualityThresholdAtom,
	useFacePreviews,
} from "@/store/importing/culling-state"
import { ProjectItem } from "@/store/project/projects-service"
import clsx from "clsx"
import { useAtomValue } from "jotai"
import { selectAtom } from "jotai/utils"
import { EyeStatus } from "@/lib/ai/detection/face-scoring"

import React, {
	FC,
	PropsWithChildren,
	PropsWithoutRef,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react"
import { Icon } from "./icons/Icon"
import { capitalized } from "@/utils/string"
import { ControlGroup } from "./ControlGroup"
import { clamp, equals } from "@/utils/math"
import { vec2 } from "gl-matrix"
import { padFaceRect } from "@/store/importing/culling-utils"
import { Button } from "./ui/Button"
import { isAIDebugEnabled } from "@/utils/measurement/feature-flag"
import { isPhotoLoadedIntoEditorAtom } from "@/store/editor/photo-loading"
import { expandedPanelsAtom } from "@/store/ui-views-state"
import { PreviewSize } from "@/store/previews/previews-types"
import { getEditor } from "@/utils/useEditor"
import { getGroundTruth } from "@/utils/culling-evaluation"

const facesExpandedAtom = selectAtom(expandedPanelsAtom, (panels) => panels["faces"] ?? true)

const ZOOM_STEP = 0.2
const ZOOM_MIN = 1
const ZOOM_MAX = 3

const MIN_TWO_COLS_COUNT = 2

const cancellables = new Set<string>()

export const FacesPanel = (props: {
	currentPhoto: PhotoRecord | null
	project: ProjectItem
	width: number
	scrollContainerRef?: React.RefObject<HTMLDivElement | null>
}) => {
	const { currentPhoto, project, width, scrollContainerRef } = props
	const { t } = useTranslation("components")

	const facePreviews = useFacePreviews(project, currentPhoto)
	const cullingInfo = useAtomValue(currentCullingInfoAtom)
	const faceInfos = cullingInfo?.faceInfo ?? []
	const scoreItem = cullingInfo?.scoreItem
	const isPhotoLoaded = useAtomValue(isPhotoLoadedIntoEditorAtom)
	const faceRejectThreshold = useAtomValue(poorFaceQualityThresholdAtom)
	const blurryRejectThreshold = useAtomValue(blurryPhotoThresholdAtom)
	const isFacePanelExpanded = useAtomValue(facesExpandedAtom)
	const panelRef = useRef<HTMLDivElement>(null)
	const facesGridRef = useRef<HTMLDivElement>(null)
	const [useTwoColumns, setUseTwoColumns] = useState(false)
	const faceCount = facePreviews ? facePreviews.length : 0

	useEffect(() => {
		if (!currentPhoto) {
			return
		}
		if (!isPhotoLoaded || !cullingInfo?.scoreItem) {
			return
		}

		if (
			facePreviews &&
			facePreviews.length > 0 &&
			facePreviews[0]?.size === PreviewSize.fullSize
		) {
			return
		}

		if (cancellables.has(currentPhoto.id)) {
			return
		}

		const editor = getEditor()

		cancellables.add(currentPhoto.id)

		extractFacesFromLoadedPhotoIfNeeded({
			projectId: project.id,
			photo: currentPhoto,
			loadedPhoto: editor.photo,
			isForAutoExposure: false,
		}).then(() => {
			cancellables.delete(currentPhoto.id)
		})
	}, [isPhotoLoaded, facePreviews, currentCullingInfoAtom])

	const statusMessage = () => {
		if (!cullingInfo) {
			return t("facesPanel.detectingFaces")
		} else if (faceInfos.length === 0) {
			return t("facesPanel.noFacesDetected")
		} else if (facePreviews && facePreviews.length === 0) {
			return t("facesPanel.loadingFaces")
		} else {
			return undefined
		}
	}

	const getBgColor = (
		scoreType: "face" | "lighting" | "focus",
		score: number,
		faceRejectThreshold: number,
		blurryRejectThreshold: number,
	) => {
		if (scoreType === "face") {
			return faceScoreColor(score, faceRejectThreshold)
		} else if (scoreType === "lighting") {
			if (score <= 1.0) {
				return "bg-red-500"
			} else if (score >= 2.5) {
				return "bg-green-500"
			} else {
				return "bg-yellow-500"
			}
		} else if (scoreType === "focus") {
			return focusScoreColor(score, blurryRejectThreshold)
		}
		return ""
	}

	const getTextColor = (score: number) => {
		switch (Math.round(score)) {
			case 1:
				return "text-red-500"
			case 2:
			case 3:
				return "text-yellow-500"
			case 4:
			case 5:
				return "text-green-500"
			default:
				return ""
		}
	}

	const renderScore = (
		scoreType: "face" | "lighting" | "focus",
		title: string,
		score: number,
	) => {
		const formattedScore = score.toFixed(1)
		const scoreWidth = `${Math.min(Math.max(score, 0), 5) * 20}%`
		const bgColor = getBgColor(scoreType, score, faceRejectThreshold, blurryRejectThreshold)
		const textColor = bgColor.replace("bg-", "text-")
		return (
			<div
				aria-label={t("facesPanel.score", { title: title })}
				className="grid w-full grid-cols-[72px_minmax(0,1fr)] items-center gap-2"
			>
				<div className="truncate">{title}</div>
				<div className="flex min-w-0 flex-row items-center justify-end space-x-2">
					<div className="relative min-w-0 flex-1">
						<div
							className={clsx("inset-0 h-2 rounded-full", bgColor)}
							style={{ width: scoreWidth }}
						></div>
						<div className="absolute inset-0 h-2 w-full rounded-full border border-base-700"></div>
					</div>
					<div
						className={clsx("w-6 shrink-0 text-center", textColor)}
						aria-label={t("facesPanel.score2", { var0: title.toLowerCase() })}
					>
						{formattedScore}
					</div>
				</div>
			</div>
		)
	}

	const renderScoresGroup = () => {
		return (
			scoreItem && (
				<ControlGroup
					expandKey="cull_score"
					name={t("facesPanel.panelScore")}
					isExpanded
					accessoryView={
						isAIDebugEnabled() && (
							<Button
								className={clsx(
									"mr-1 text-center text-sm font-bold",
									getTextColor(scoreItem.overall),
								)}
								trackingLabel={"overall_score"}
								tooltip={t("facesPanel.tooltipOverallScore")}
								variant="custom"
								size="custom"
							>
								{scoreItem.overall}
							</Button>
						)
					}
				>
					<div className="flex w-full flex-col space-y-2 py-2 text-xs">
						{scoreItem.face &&
							renderScore("face", t("facesPanel.scoreFace"), scoreItem.face)}
						{renderScore("lighting", t("facesPanel.scoreLighting"), scoreItem.lighting)}
						{renderScore("focus", t("facesPanel.scoreFocus"), scoreItem.blur)}
					</div>
				</ControlGroup>
			)
		)
	}

	const renderGTScoresGroup = () => {
		if (!currentPhoto) return null
		const gt = getGroundTruth(currentPhoto.fileName)
		if (!gt) return null
		return (
			<ControlGroup expandKey="gt_score" name={t("facesPanel.panelGtScore")} isExpanded>
				<div className="flex w-full flex-col space-y-2 py-2 text-xs">
					{gt.face_score !== -1 &&
						renderScore("face", t("facesPanel.scoreFace"), gt.face_score)}
					{renderScore("focus", t("facesPanel.scoreFocus"), gt.focus_score)}
				</div>
			</ControlGroup>
		)
	}

	const updateColumns = useCallback(() => {
		const scrollContainer = scrollContainerRef?.current
		const panel = panelRef.current
		const grid = facesGridRef.current
		if (!scrollContainer || !panel || !grid) {
			return
		}

		const gridRect = grid.getBoundingClientRect()
		if (gridRect.width === 0) {
			return
		}

		const gridStyles = window.getComputedStyle(grid)
		const parsedRowGap = Number.parseFloat(gridStyles.rowGap || gridStyles.gap || "0")
		const parsedPaddingTop = Number.parseFloat(gridStyles.paddingTop || "0")
		const rowGap = Number.isFinite(parsedRowGap) ? parsedRowGap : 0
		const paddingTop = Number.isFinite(parsedPaddingTop) ? parsedPaddingTop : 0

		const rows = faceCount
		const tileSize = gridRect.width
		const oneColumnHeight =
			rows === 0 ? paddingTop : paddingTop + tileSize * rows + rowGap * (rows - 1)

		const baseHeight =
			panel.getBoundingClientRect().height - grid.getBoundingClientRect().height
		const hypotheticalHeight = baseHeight + oneColumnHeight
		const availableHeight = scrollContainer.clientHeight
		const shouldUseTwo =
			faceCount >= MIN_TWO_COLS_COUNT && hypotheticalHeight > availableHeight + 1

		setUseTwoColumns((prev) => (prev === shouldUseTwo ? prev : shouldUseTwo))
	}, [faceCount, scrollContainerRef])

	useLayoutEffect(() => {
		updateColumns()
	}, [updateColumns, width, isFacePanelExpanded])

	useEffect(() => {
		const scrollContainer = scrollContainerRef?.current
		const panel = panelRef.current
		const grid = facesGridRef.current
		if (!scrollContainer || !panel || !grid) {
			return
		}

		const observer = new ResizeObserver(() => {
			updateColumns()
		})

		observer.observe(scrollContainer)
		observer.observe(panel)
		observer.observe(grid)

		return () => {
			observer.disconnect()
		}
	}, [scrollContainerRef, updateColumns, width, isFacePanelExpanded])

	const isFullHeight = statusMessage() !== undefined
	return (
		<div ref={panelRef} className={clsx(isFullHeight && "h-full", "flex w-full flex-col")}>
			{renderScoresGroup()}
			{isAIDebugEnabled() && renderGTScoresGroup()}
			<ControlGroup
				expandKey="faces"
				name={t("facesPanel.panelFaceFocus")}
				isExpanded
				hideSeparator
				flex
			>
				{statusMessage() && (
					<div
						className={clsx(
							"flex size-full shrink-0 flex-col justify-center py-2 text-center text-sm text-base-400 transition-opacity duration-150",
							isFacePanelExpanded ? "opacity-100 delay-150" : "opacity-0 delay-0",
						)}
					>
						{statusMessage()}
					</div>
				)}
				<FacesGrid
					count={faceCount}
					width={width}
					columns={useTwoColumns && faceCount >= MIN_TWO_COLS_COUNT ? 2 : 1}
					gridRef={facesGridRef}
				>
					{facePreviews?.map(({ url }, index) => (
						<FacesGridItem
							key={`${currentPhoto?.id}-${index}`}
							src={url}
							photoId={currentPhoto?.id ?? "unknown"}
							index={index}
							faceInfo={faceInfos[index]}
							single={facePreviews && facePreviews.length === 1}
						/>
					))}
				</FacesGrid>
			</ControlGroup>
		</div>
	)
}

const FacesGrid: FC<
	PropsWithChildren<{
		count: number
		width: number
		columns: 1 | 2
		gridRef?: React.RefObject<HTMLDivElement | null>
	}>
> = ({ count, width, columns, gridRef, children }) => {
	const className = clsx(
		"w-full pt-2",
		{
			"grid-cols-1": columns === 1 || count === 0,
			"grid-cols-2": columns === 2 && count > 0,
		},
		"grid gap-2",
	)

	return (
		<div className="mx-[-12px] flex w-full" style={{ width }}>
			<div className={className} ref={gridRef}>
				{children}
			</div>
		</div>
	)
}

const FacesGridItem: FC<
	PropsWithoutRef<{
		src: string
		photoId: string
		index: number
		faceInfo: FaceCullingInfo | undefined
		single: boolean
	}>
> = ({ src, photoId, index, faceInfo, single }) => {
	const imgRef = useRef<HTMLImageElement>(null)
	const dragRef = useRef<[number, number] | null>(null)
	const [isHovered, setIsHovered] = useState(false)
	const faceRejectThreshold = useAtomValue(poorFaceQualityThresholdAtom)
	const blurryRejectThreshold = useAtomValue(blurryPhotoThresholdAtom)
	let previewOrigin: vec2 = [0.5, 0.5]
	let maxZoom = 2.0
	const paddedFace = faceInfo ? padFaceRect(faceInfo.faceRect) : undefined
	if (paddedFace) {
		maxZoom = paddedFace.maxZoom
		previewOrigin = paddedFace.internalAnchor
	}
	const posRef = useRef<[number, number]>([0, 0])
	const [imgLoaded, setImgLoaded] = useState(false)
	const needComputePos = !equals(previewOrigin[0], 0.5) || !equals(previewOrigin[1], 0.5)

	const zoom = (() => {
		if (!faceInfo?.faceRect || single) {
			return 1.2
		}

		const rect = faceInfo.faceRect

		if (
			rect.x < 0.05 ||
			rect.x + rect.width > 0.95 ||
			rect.y === 0.05 ||
			rect.y + rect.height === 0.95
		) {
			return 1.2
		}

		if (rect.width > 0.5 || rect.height > 0.5) {
			return 1.6
		}

		if (rect.width > 0.4 || rect.height > 0.4) {
			return 1.7
		}

		if (rect.width > 0.3 || rect.height > 0.3) {
			return 1.8
		}

		return 1.9
	})()

	const zoomRef = useRef<number>(Math.min(zoom, maxZoom))

	const getBoundariesAndTranslations = () => {
		const container = imgRef.current?.parentElement

		if (!container) {
			return { maxX: 0, minX: 0, maxY: 0, minY: 0, translateX: 0, translateY: 0 }
		}
		const image = imgRef.current
		if (!image) {
			return { maxX: 0, minX: 0, maxY: 0, minY: 0, translateX: 0, translateY: 0 }
		}

		const imgW = image.naturalWidth
		const imgH = image.naturalHeight

		const containerWidth = container.offsetWidth
		const containerHeight = container.offsetHeight

		const scaleX = containerWidth / imgW
		const scaleY = containerHeight / imgH
		const initialScale = Math.max(scaleX, scaleY)

		const scaledImageWidth = imgW * initialScale * zoomRef.current
		const scaledImageHeight = imgH * initialScale * zoomRef.current

		const maxX = -containerWidth / 2 + scaledImageWidth / 2
		const minX = -maxX
		const maxY = -containerHeight / 2 + scaledImageHeight / 2
		const minY = -maxY

		// Translate the face center to the container's center, but clamp to avoid showing out-of-image empty space
		const translateX = clamp((0.5 - previewOrigin[0]) * scaledImageWidth, minX, maxX)
		const translateY = clamp((0.5 - previewOrigin[1]) * scaledImageHeight, minY, maxY)

		return { maxX, minX, maxY, minY, translateX, translateY }
	}

	const adjustPositionToCenterFace = () => {
		if (!needComputePos) {
			posRef.current = [0, 0]
		} else {
			const image = imgRef.current
			const container = imgRef.current?.parentElement
			if (container && image) {
				const { translateX, translateY } = getBoundariesAndTranslations()

				posRef.current = [translateX, translateY] as vec2
			}
		}
	}

	const updatePosition = (pos: vec2) => {
		posRef.current = pos

		if (!imgRef.current) {
			return
		}

		imgRef.current.style.transform = `translate(${posRef.current[0]}px, ${posRef.current[1]}px) scale(${zoomRef.current})`
	}

	const handleWheel = (e: React.WheelEvent) => {
		if (!imgRef.current) {
			return
		}

		// Trackpad pinch zoom (and ctrl+wheel) sets ctrlKey=true in Chrome/Edge.
		// Ignore normal scroll so the grid can scroll without zooming.
		if (!e.ctrlKey) {
			return
		}

		e.preventDefault()

		if (e.deltaY < 0) {
			zoomRef.current = Math.min(ZOOM_MAX, zoomRef.current + ZOOM_STEP)
		} else if (e.deltaY > 0) {
			zoomRef.current = Math.max(ZOOM_MIN, zoomRef.current - ZOOM_STEP)
		}

		adjustPositionToCenterFace()

		updatePosition(posRef.current)
	}

	const handleDrag = (e: MouseEvent) => {
		if (!dragRef.current || !imgRef.current || !posRef.current || !zoomRef.current) {
			return
		}

		const deltaX = e.clientX - dragRef.current[0]
		const deltaY = e.clientY - dragRef.current[1]

		const { maxX, minX, maxY, minY } = getBoundariesAndTranslations()

		const pos: vec2 = [
			clamp(posRef.current[0] + deltaX, minX, maxX),
			clamp(posRef.current[1] + deltaY, minY, maxY),
		]

		updatePosition(pos)
		dragRef.current = [e.clientX, e.clientY]
	}

	const handleMouseUp = () => {
		dragRef.current = null

		document.removeEventListener("mousemove", handleDrag)
		document.removeEventListener("mouseup", handleMouseUp)
	}

	const handleMouseDown = (e: React.MouseEvent) => {
		dragRef.current = [e.clientX, e.clientY]

		document.addEventListener("mousemove", handleDrag)
		document.addEventListener("mouseup", handleMouseUp)
	}

	return (
		<div
			className="relative aspect-square w-full"
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			data-testid="face_preview"
		>
			<div className="flex size-full min-h-0 min-w-0 overflow-hidden">
				<img
					draggable={false}
					className="min-h-full min-w-full shrink-0 grow-0 cursor-grab overflow-visible object-cover"
					style={{
						visibility: imgLoaded || !needComputePos ? "visible" : "hidden",
						objectPosition: `50% 50%`,
						transformOrigin: `50% 50%`,
						willChange: "transform",
						transform: `translate(${posRef.current[0]}px, ${posRef.current[1]}px) scale(${zoomRef.current})`,
					}}
					src={src}
					ref={imgRef}
					onMouseDown={handleMouseDown}
					onWheel={handleWheel}
					onLoad={() => {
						adjustPositionToCenterFace()
						setImgLoaded(true)
					}}
					onError={() => {
						console.error("[Face preview][debug] face_preview_img_error", {
							photoId,
							index,
							srcPrefix: src.slice(0, 64),
							currentSrc: imgRef.current?.currentSrc ?? "",
						})
					}}
				/>
			</div>
			{faceInfo && (
				<div className="absolute bottom-0 z-10 flex flex-col space-y-1 overflow-visible px-2 py-1">
					<div className="flex w-fit flex-row items-center space-x-2 overflow-visible rounded-sm bg-black/50 text-base-800 transition-all dark:text-base-200">
						{(faceInfo.focusScore ?? 0) >= 2 && isNotOccluded(faceInfo) && (
							<Icon
								name="eye"
								size={12}
								className={clsx(
									eyeScoreColor(faceInfo, faceRejectThreshold),
									isHovered ? "rounded-s-sm" : "rounded-sm",
									"size-5 p-1",
								)}
							/>
						)}

						{isHovered && (
							<div
								className="text-nowrap pr-2 text-xs"
								data-testid="face_eye_description"
							>
								{eyeScoreText(faceInfo)}
							</div>
						)}
					</div>

					<div className="flex w-fit flex-row items-center space-x-2 overflow-visible rounded-sm bg-black/50 text-base-800 transition-all dark:text-base-200">
						<div
							data-testid="face_focus_value"
							className={clsx(
								focusDisplay(faceInfo.focusScore, blurryRejectThreshold).color,
								isHovered ? "rounded-s-sm" : "rounded-sm",
								"size-5 justify-center pt-0.5 text-center align-middle text-xs font-medium",
							)}
						>
							{Math.round(faceInfo.focusScore ?? 0)}
						</div>
						{isHovered && (
							<div className="overflow-visible text-nowrap pr-2 text-xs">
								{focusDisplay(faceInfo.focusScore, blurryRejectThreshold).text}
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	)
}

const isNotOccluded = (faceInfo: FaceCullingInfo) => {
	const eyeStatus = faceInfo.eyeStatus ?? faceInfo.eyeScore ?? 0
	return (
		eyeStatus !== EyeStatus.occluded &&
		eyeStatus !== EyeStatus.tooBlurry &&
		eyeStatus !== EyeStatus.notAFace
	)
}

// const eyeScoreColorPerCategory = (faceInfo: FaceCullingInfo) => {
// 	const eyeStatus = faceInfo.eyeStatus ?? faceInfo.eyeScore ?? 0
// 	if (
// 		eyeStatus === EyeStatus.occluded ||
// 		eyeStatus === EyeStatus.tooBlurry ||
// 		eyeStatus === EyeStatus.notAFace
// 	) {
// 		return "bg-base-400"
// 	}

// 	if (eyeStatus === EyeStatus.closed) {
// 		return "bg-red-500"
// 	}

// 	if (
// 		eyeStatus === EyeStatus.halfOpen ||
// 		eyeStatus === EyeStatus.barelyOpen ||
// 		eyeStatus === EyeStatus.midBlink ||
// 		eyeStatus === EyeStatus.squinting
// 	) {
// 		return "bg-yellow-500"
// 	}

// 	if (
// 		eyeStatus === EyeStatus.fullyOpen ||
// 		eyeStatus === EyeStatus.posingLookDown ||
// 		eyeStatus === EyeStatus.posingSmile ||
// 		eyeStatus === EyeStatus.posingBeClose ||
// 		eyeStatus === EyeStatus.sunglasses
// 	) {
// 		return "bg-green-500"
// 	}

// 	return "bg-base-400"
// }

const eyeScoreColor = (faceInfo: FaceCullingInfo, faceRejectThreshold: number) => {
	if (isNotOccluded(faceInfo)) {
		const expressionScore = faceInfo.expressionScore ?? faceInfo.eyeEstScore ?? 0
		return faceScoreColor(expressionScore, faceRejectThreshold)
	}

	return "bg-base-400"
}

const faceScoreColor = (score: number, faceRejectThreshold: number) => {
	const greenThresholdMap = {
		[FaceRejectThreshold.Lenient]: 3,
		[FaceRejectThreshold.Balanced]: 3.5,
		[FaceRejectThreshold.Strict]: 4,
	}

	if (score <= faceRejectThreshold) {
		return "bg-red-500"
	} else if (score >= greenThresholdMap[faceRejectThreshold]) {
		return "bg-green-500"
	} else {
		return "bg-yellow-500"
	}
}

const eyeScoreText = (faceInfo: FaceCullingInfo) => {
	if (faceInfo.focusScore != undefined && faceInfo.focusScore < 2) return null

	const eyeStatus = faceInfo.eyeStatus ?? faceInfo.eyeScore ?? 0
	const text = [
		i18n.t("facesPanel.eyeClosed", { ns: "components" }),
		i18n.t("facesPanel.eyeBarelyOpen", { ns: "components" }),
		i18n.t("facesPanel.eyePartiallyOpen", { ns: "components" }),
		i18n.t("facesPanel.eyeFullyOpen", { ns: "components" }),
		i18n.t("facesPanel.eyeBlinked", { ns: "components" }),
		i18n.t("facesPanel.eyeLookingDown", { ns: "components" }),
		i18n.t("facesPanel.eyeSmiling", { ns: "components" }),
		i18n.t("facesPanel.eyeIntimatePose", { ns: "components" }),
		i18n.t("facesPanel.eyeSunglasses", { ns: "components" }),
		"", // Occluded
		"", // Too Blurry
		"", // Not a Face
		i18n.t("facesPanel.eyeSquinting", { ns: "components" }),
	]

	const result = text[eyeStatus]
	return result ? capitalized(result) : null
}

const focusScoreColor = (blurScore: number, blurryRejectThreshold: number) => {
	const greenThresholdMap = {
		[BlurryRejectThreshold.Lenient]: 3,
		[BlurryRejectThreshold.Balanced]: 3.5,
		[BlurryRejectThreshold.Strict]: 4,
	}

	if (blurScore <= blurryRejectThreshold) {
		return "bg-red-500"
	} else if (blurScore >= greenThresholdMap[blurryRejectThreshold]) {
		return "bg-green-500"
	} else {
		return "bg-yellow-500"
	}
}

const focusDisplay = (rating: number | undefined, blurryRejectThreshold: number) => {
	if (rating == undefined) return { text: "", color: "" }
	const blurScore = (rating + 0.5) / 2
	const blurColor = focusScoreColor(blurScore, blurryRejectThreshold)
	if (rating < 2)
		return { text: i18n.t("facesPanel.focusOutOf", { ns: "components" }), color: blurColor }
	if (rating < 6)
		return { text: i18n.t("facesPanel.focusSoft", { ns: "components" }), color: blurColor }
	if (rating < 8)
		return { text: i18n.t("facesPanel.focusGood", { ns: "components" }), color: blurColor }
	else return { text: i18n.t("facesPanel.focusGreat", { ns: "components" }), color: blurColor }
}
